import { Timestamp } from 'firebase/firestore';

// Status is worked out from the ticks and the TMDB summary, never stored, so
// a new season moves a Caught Up show back to In Progress on its own.
export type ShowStatus =
  | 'wantToWatch'
  | 'inProgress'
  | 'caughtUp'
  | 'completed'
  | 'givenUp';

export interface SeasonSummary {
  n: number;
  name: string;
  episodeCount: number;
  // Episodes out so far, from TMDB's last aired episode.
  aired: number;
}

export interface TmdbSummary {
  status: string;
  lastAirDate: string | null;
  nextAirDate: string | null;
  seasons: SeasonSummary[];
  checkedAt: Timestamp;
}

export interface TVShow {
  id: string;
  tmdbId: number;
  title: string;
  year: number;
  image: string;
  order: number;
  dateAdded: Timestamp;
  // Ticked episode numbers, keyed by season number.
  watched: Record<string, number[]>;
  givenUp?: boolean;
  // Rewatches started; the current one is still running while `rewatching`.
  rewatches: number;
  rewatching?: boolean;
  // Where TMDB was up to the last time every aired episode was ticked.
  // A later air date means new episodes have come out since.
  caughtUpThrough?: { season: number; airDate: string | null };
  tmdb: TmdbSummary;
}

export const STATUS_LABELS: Record<ShowStatus, string> = {
  wantToWatch: 'Want to Watch',
  inProgress: 'In Progress',
  caughtUp: 'Caught Up',
  completed: 'Completed',
  givenUp: 'Given Up',
};

export const SHOW_STATUSES: ShowStatus[] = [
  'wantToWatch',
  'inProgress',
  'caughtUp',
  'completed',
  'givenUp',
];

export const EMPTY_MESSAGES: Record<ShowStatus, string> = {
  wantToWatch: 'Nothing waiting to be started.',
  inProgress: 'Nothing in progress right now.',
  caughtUp: "You're not caught up on anything still airing.",
  completed: 'No completed shows yet.',
  givenUp: 'Nothing given up on.',
};

export const COLLECTION_NAME = 'TVShows';

const FINISHED_TMDB_STATUSES = ['Ended', 'Canceled'];

export const hasEnded = (show: TVShow) =>
  FINISHED_TMDB_STATUSES.includes(show.tmdb.status);

export const seasonWatched = (show: TVShow, season: SeasonSummary) =>
  Math.min(show.watched[season.n]?.length ?? 0, season.aired);

export const progress = (show: TVShow) => {
  let seen = 0;
  let aired = 0;
  show.tmdb.seasons.forEach((season) => {
    seen += seasonWatched(show, season);
    aired += season.aired;
  });
  return { seen, aired };
};

export const isCaughtUp = (show: TVShow) => {
  const { seen, aired } = progress(show);
  return aired > 0 && seen >= aired;
};

export const deriveStatus = (show: TVShow): ShowStatus => {
  if (show.givenUp) return 'givenUp';
  const { seen, aired } = progress(show);
  if (seen === 0 && !show.rewatching) return 'wantToWatch';
  if (aired > 0 && seen >= aired) {
    return hasEnded(show) ? 'completed' : 'caughtUp';
  }
  return 'inProgress';
};

// Badge for a show you'd caught up on that has had episodes since.
export const newEpisodesBadge = (show: TVShow): string | null => {
  const caughtUp = show.caughtUpThrough;
  if (!caughtUp || show.givenUp || isCaughtUp(show)) return null;
  const { lastAirDate } = show.tmdb;
  if (!lastAirDate || !caughtUp.airDate || lastAirDate <= caughtUp.airDate) {
    return null;
  }
  const latestSeason = Math.max(
    0,
    ...show.tmdb.seasons.filter((s) => s.aired > 0).map((s) => s.n)
  );
  return latestSeason > caughtUp.season ? 'New season' : 'New episodes';
};

// The first season with aired episodes still to watch.
export const currentSeason = (show: TVShow) =>
  show.tmdb.seasons.find((s) => seasonWatched(show, s) < s.aired);
